import { JSX, useMemo } from "react";

/**
 * Data element for a single segment of a pie chart.
 */
export interface PieSegmentData {
  /**
   * The color to fill the segment with.
   * Must be a browser recognizable color format.
   */
  fill: string;

  /**
   * The percent of the segment in decimal format.
   * Must be a value between 0 and 1 inclusive.
   */
  percent: number;
}

/**
 * Renders a pie chart as an SVG element.
 */
export default function Pie({ segments }: { segments: PieSegmentData[] }): JSX.Element {
  // Translate segments into PieSegmentProps.
  // Track the cumulative percent used for rotational calculations.
  // Memoize to prevent unnecessary rendering.
  const cumulativeSegments = useMemo(() => {
    const values: PieSegmentProps[] = [];

    let cumulativePercent = 0;
    for (const segment of segments) {
      values.push({ cumulativePercent, fill: segment.fill, percent: segment.percent });
      cumulativePercent += segment.percent;
    }

    return values;
  }, [segments]);

  return (
    <svg className="pie" viewBox="0 0 200 200">
      {cumulativeSegments.map((segmentProps, i) => (
        <PieSegment key={i} {...segmentProps} />
      ))}
    </svg>
  );
}

/**
 * The set of elements necessary to render a single pie segment.
 */
interface PieSegmentProps {
  /**
   * How much of the pie chart has been rendered; formatted as a decimal percentage.
   */
  cumulativePercent: number;

  /**
   * The browser recognizable color used for segment fill.
   */
  fill: string;

  /**
   * The percent of the pie chart this segment will take up.
   */
  percent: number;
}

/**
 * Renders a single segment of the pie chart, rotated into correct position.
 * Rendered as a path element unless it is 100% (1.0) of the pie.
 */
function PieSegment({ cumulativePercent, fill, percent }: PieSegmentProps) {
  // Memoize the transform to prevent unnecessary renders.
  const transform = useMemo(() => {
    return `translate(100, 100) rotate(180 0 0) rotate(${360 * cumulativePercent} 0 0)`;
  }, [cumulativePercent]);

  if (percent === 1) {
    return <circle cx="100" cy="100" r="100" fill={fill} />;
  }

  return (
    <path
      className="pie-segment"
      d={`M 0 0 L 0 100 A 100 100 0 ${percent < 0.5 ? 0 : 1} 1 ${x(100, percent) * -1} ${y(100, percent)} Z`}
      transform={transform}
      fill={fill}
    />
  );
}

/**
 * Transforms a radius and percent of a circular into an x coordinate.
 * @param radius The radius of the circle.
 * @param percent The percent of the circle's border to traverse.
 */
function x(radius: number, percent: number) {
  return radius * Math.sin(radians(percent));
}

/**
 * Transforms a radius and percent of a circular into a y coordinate.
 * @param radius The radius of the circle.
 * @param percent The percent of the circle's border to traverse.
 */
function y(radius: number, percent: number) {
  return radius * Math.cos(radians(percent));
}

/**
 * Transforms a percent to radians.
 * @param percent The percent, in decimal format ([0, 1.0]).
 */
function radians(percent: number) {
  return 2 * Math.PI * percent;
}
